/**
 * A single toast in the bottom-right notification layer (Flow F Frames 2–3;
 * ``chrome-spec.md`` §"Toast layer").
 *
 * # The auto-dismiss timer
 *
 * The store resolves ``durationMs`` once at raise time; this component owns
 * the countdown. Hovering or focusing within the toast **pauses** it (Flow
 * F §Accessibility — "Toast auto-dismiss can be paused by hovering or
 * focusing within the toast"), and the unspent remainder is banked on
 * pause so resuming *continues* the countdown rather than restarting it —
 * an operator who brushes the toast twice does not get a toast that lives
 * forever.
 *
 * A ``durationMs`` of ``null`` is sticky: no timer at all.
 *
 * The host keys this component on ``id:createdAt``, so a dedupe replacement
 * remounts it and the timer restarts from scratch without this component
 * needing to detect the change.
 *
 * # Close vs. Dismiss
 *
 * The ``×`` calls ``onClose`` — local only. A SAPIENT alert's system-wide
 * dismissal (ADR-029 §7) is a separate, explicitly-labelled entry in
 * ``actions``. See :file:`notification-types.ts` §"Local close vs
 * system-wide dismissal".
 *
 * Cross-references:
 *   - storyboard Flow F Frames 2–3 + §Accessibility + §Behavior notes.
 *   - ``chrome-spec.md`` §"Toast layer" / §"Toast layer (production)".
 *   - REQ-UI-046 (legibility); REQ-UI-059 (keyboard operability).
 *   - ADR-029 §5 (severity → surface).
 */
import { X } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { severityChrome } from "@/notifications/notification-chrome";
import { notificationAriaRole } from "@/notifications/notification-timing";
import type { Notification, NotificationId } from "@/notifications/notification-types";

/** Props for {@link Toast}. */
export interface ToastProps {
  /** The notification to render. */
  readonly notification: Notification;
  /** Remove it from this operator's view (local only). */
  readonly onClose: (id: NotificationId) => void;
  /** Suspend / resume the auto-dismiss countdown. */
  readonly onPauseChange: (id: NotificationId, paused: boolean) => void;
}

/**
 * One toast: severity icon, title, optional body, optional actions, and a
 * close affordance.
 *
 * Announced via ``role="status"`` (info / success / warning) or
 * ``role="alert"`` (error / critical) — the role carries the implicit
 * ``aria-live`` politeness, so no redundant ``aria-live`` is set.
 * ``Escape`` closes the toast when focus is inside it (REQ-UI-059).
 */
export function Toast({ notification, onClose, onPauseChange }: ToastProps): React.ReactElement {
  const { id, severity, title, body, actions, durationMs, paused } = notification;
  const { Glyph, srPrefix, accentClass } = severityChrome(severity);

  /** Unspent countdown, banked across pauses. */
  const remainingRef = useRef<number>(durationMs ?? 0);

  const close = useCallback(() => onClose(id), [onClose, id]);

  useEffect(() => {
    // Sticky, or paused by hover/focus — no countdown running.
    if (durationMs === null || paused) {
      return;
    }
    const startedAt = Date.now();
    const timer = window.setTimeout(close, remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      // Bank the remainder so a resume continues instead of restarting.
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [durationMs, paused, close]);

  return (
    <div
      role={notificationAriaRole(severity)}
      data-testid={`toast-${id}`}
      data-severity={severity}
      className={cn(
        "pointer-events-auto w-80 rounded-md border border-border bg-bg-overlay",
        "px-4 py-3 shadow-lg",
        "animate-in slide-in-from-right-4 duration-200",
      )}
      onMouseEnter={() => onPauseChange(id, true)}
      onMouseLeave={() => onPauseChange(id, false)}
      onFocus={() => onPauseChange(id, true)}
      onBlur={() => onPauseChange(id, false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          close();
        }
      }}
    >
      <div className="flex items-start gap-3">
        <Glyph aria-hidden className={cn("mt-0.5 shrink-0", accentClass)} size={18} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">
            {/* Spoken, not shown — the icon carries the visual signal. */}
            <span className="sr-only">{srPrefix}: </span>
            {title}
          </p>
          {body !== undefined && body !== "" && (
            <p className="mt-1 text-xs text-text-secondary">{body}</p>
          )}
          {actions !== undefined && actions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-3">
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={action.onSelect}
                  className={cn(
                    "rounded text-xs font-medium underline-offset-2 hover:underline",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    action.systemWide === true ? "text-destructive" : "text-brand",
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          // "Close" not "Dismiss": dismissal is the system-wide alert action
          // and the two must not read as synonyms to a screen-reader user.
          aria-label="Close notification"
          className={cn(
            "-mr-1 -mt-1 shrink-0 rounded p-1 text-text-muted hover:text-text-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <X aria-hidden size={14} />
        </button>
      </div>
    </div>
  );
}
